import { HttpErrorResponse } from '@angular/common/http';
import { Store } from '@sama/app-state';
import { isDefined } from '@sama/js-core';
import { Material } from 'three';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { ShapeState } from '../../cross-team-shared/annotation/shared/state/shape-state';

export function isLeftButton(event: MouseEvent) {
  return event.button === 0;
}

/**
 * Timing can be a pain in Angular. Use this to delay execution of some code
 * without going all wthe way to `$timeout`. This will queue up `fn` on the
 * "microtask queue", which means it will run later, but still before the
 * javascript event thread yields. This is nice for tests, because it means
 * you don't have to add `$timeout.flush(0)`.
 */
export function delayExecution(fn: () => void) {
  return Promise.resolve().then(fn);
}

export interface ObjectWith<T> {
  [key: string]: T;
}

export class AssetLoadError extends Error {
  constructor(
    message: string,
    public url?: string,
  ) {
    super(message);
  }
}

export function determineAssetLoadErrorMessage(error: Error) {
  const status = error instanceof HttpErrorResponse && error.status;
  switch (status) {
    case 403:
      return 'Access denied';
    case 404:
      return `Asset does not exist at the given url`;
    case 0:
      return `Asset could not be accessed`;
    default:
      return `Asset failed to load. Please reload.`;
  }
}

export function getShapeForEventTracking(shape: ShapeState) {
  return pickExisting(shape, 'index', 'type', 'source');
}

export function toggleState(store: Store<boolean>) {
  store.setUsing((value) => !value);
}

type Nil = null | undefined;

/**
 * Like `pick()` from micro-dash, but does not include keys that do not exist in
 * `object`. (`pick()` will include the key, with the value `undefined`.)
 *
 * Modified from https://github.com/simontonsoftware/micro-dash/blob/master/projects/micro-dash/src/lib/object/pick.ts
 */
export function pickExisting<T, K extends keyof T>(
  object: T | Nil,
  ...paths: K[]
): T extends Nil ? StrictRecord<string, never> : Pick<T, K> {
  const result: any = {};
  if (object != null) {
    for (const path of paths) {
      if (object.hasOwnProperty(path)) {
        result[path] = object[path];
      }
    }
  }
  return result;
}

export function isLineMaterial(line: Material): line is LineMaterial {
  return (
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- type guard, this is not always truthy if parameter is not LineMaterial
    (line as LineMaterial).isLineMaterial &&
    isDefined((line as LineMaterial).resolution)
  );
}
